import React, { useEffect, useState } from 'react';
import { nextSaleBoundary, formatCentral } from '../utils/productSale';

const MAX_DELAY = 2147483000;

// Re-render the caller exactly when any of the given products' sales start or end (tab left open included).
export const useSaleBoundaryRerender = (products) => {
  const [, setTick] = useState(0);
  const list = Array.isArray(products) ? products : products ? [products] : [];
  const next = nextSaleBoundary(list);
  useEffect(() => {
    if (next == null) return undefined;
    const id = setTimeout(() => setTick((t) => t + 1), Math.min(MAX_DELAY, Math.max(0, next - Date.now()) + 50));
    return () => clearTimeout(id);
  }, [next]);
};

export const SaleBadge = ({ testid, style }) => (
  <span
    data-testid={testid}
    className="rcm-sale-badge"
    style={{ display: 'inline-block', background: '#0057B8', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 4, ...style }}
  >
    SALE
  </span>
);

export const SalePrice = ({ regular, sale, testid, size = 16 }) => (
  <span data-testid={testid} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
    <span className="sr-only">Sale price ${sale.toFixed(2)}, regular price ${regular.toFixed(2)}</span>
    <s aria-hidden="true" style={{ color: 'var(--text-light, #94a3b8)', fontSize: Math.max(12, Math.round(size * 0.8)), fontWeight: 500 }} data-testid={testid && `${testid}-regular`}>${regular.toFixed(2)}</s>
    <span aria-hidden="true" style={{ color: 'var(--brand-primary)', fontSize: size, fontWeight: 700 }} data-testid={testid && `${testid}-sale`}>${sale.toFixed(2)}</span>
  </span>
);

const pad = (n) => String(n).padStart(2, '0');

// Counts down to a fixed end timestamp from the product record; disappears at zero.
export const SaleCountdown = ({ endsAt, testid, compact = false }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!endsAt || endsAt <= Date.now()) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  const left = Math.max(0, Math.floor(((endsAt || 0) - now) / 1000));
  if (left <= 0) return null;
  const d = Math.floor(left / 86400);
  const h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  return (
    <span data-testid={testid} style={{ fontSize: compact ? 11 : 13, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
      <span className="sr-only">Sale ends {formatCentral(new Date(endsAt).toISOString())}. </span>
      <span role="timer" aria-hidden="true">Ends in {d}d {pad(h)}h {pad(m)}m {pad(s)}s</span>
    </span>
  );
};
